import {
  Box,
  Card,
  Grid,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { theme, ANNOUNCEMENT_STATUS } from 'bs-unified-ui';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { Field } from '~/components/common/Formik2';
import {
  ANNOUNCEMENT_STATUS_INFO,
  DISPLAY_FORMAT,
  TIME_HOUR_DISPLAY_FORMAT,
  TIMEZONE,
} from '~/constants';
import { useAnnouncementDetailContext } from '../AnnouncementDetailContext';

const StyledStackDatePicker = styled(Stack)(() => ({
  'width': '100%',
  '& .wrap-daypicker #time-picker': {
    height: 'auto',
  },
  '& input.MuiInputBase-input': {
    color: `${theme.foreground.black} !important`,
  },
  '& .MuiSvgIcon-root': {
    color: theme.componentColors.iconDropdown,
  },
  '& .MuiFormControl-root .MuiFormHelperText-root.Mui-error': {
    color: theme.accent.red,
  },
}));

const SchedulingSection = () => {
  const { id } = useParams();
  const { values } = useFormikContext<any>();
  const { announcementDetail } = useAnnouncementDetailContext();

  const statusInfo = useMemo(() => {
    const status = announcementDetail?.status;

    if (!status) {
      return { text: '', color: '', backgroundColor: '', status: null };
    }

    const statusDetails = ANNOUNCEMENT_STATUS_INFO[status];
    return {
      text: statusDetails.text,
      color: statusDetails.color,
      backgroundColor: statusDetails.backgroundColor,
      status,
    };
  }, [announcementDetail]);

  return (
    <Card
      sx={{
        color: theme.foreground.black,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          Scheduling
        </Typography>
        {id && (
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 12,
              color: statusInfo.color,
              backgroundColor: statusInfo.backgroundColor,
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
          >
            {statusInfo.text}
          </Typography>
        )}
      </Box>
      <Grid container spacing={2} pt={2}>
        <Grid item xs={3}>
          <Typography variant='fs14' color='rgba(51, 54, 61, 0.40)'>
            Start
          </Typography>
        </Grid>
        <Grid item xs={9}>
          {id &&
          (statusInfo.status === ANNOUNCEMENT_STATUS.ARCHIVED ||
            statusInfo.status === ANNOUNCEMENT_STATUS.LIVE) ? (
            <Typography variant='fs14' color={theme.foreground.black}>
              {values.start_date && values.start_hour
                ? moment
                    .tz(
                      `${values.start_date} ${values.start_hour}`,
                      `${DISPLAY_FORMAT} ${TIME_HOUR_DISPLAY_FORMAT}`,
                      TIMEZONE,
                    )
                    .format('MMM D, YYYY [at] h:mm a')
                : '-'}
            </Typography>
          ) : (
            <Box display='flex' flexDirection='column' gap={1}>
              <StyledStackDatePicker>
                <Field.DayPicker
                  timeZone={TIMEZONE}
                  name='start_date'
                  disablePastDate
                  placeholder=''
                  dateFormat={DISPLAY_FORMAT}
                  fullWidth
                />
              </StyledStackDatePicker>
              <Field.TimeSelect
                name='start_hour'
                label=''
                placeholder=''
                timeGap={15}
                inputFormat='hh:mm A'
                outputFormatLabel={TIME_HOUR_DISPLAY_FORMAT}
                fullWidth
                checkedValue
                resizePopover
              />
            </Box>
          )}
        </Grid>

        <Grid item xs={3}>
          <Typography variant='fs14' color='rgba(51, 54, 61, 0.40)'>
            End
          </Typography>
        </Grid>
        <Grid item xs={9}>
          {id && statusInfo.status === ANNOUNCEMENT_STATUS.ARCHIVED ? (
            <Typography variant='fs14' color={theme.foreground.black}>
              {values.end_date && values.end_hour
                ? moment
                    .tz(
                      `${values.end_date} ${values.end_hour}`,
                      `${DISPLAY_FORMAT} ${TIME_HOUR_DISPLAY_FORMAT}`,
                      TIMEZONE,
                    )
                    .format('MMM D, YYYY [at] h:mm a')
                : '-'}
            </Typography>
          ) : (
            <Box display='flex' flexDirection='column' gap={1}>
              <StyledStackDatePicker>
                <Field.DayPicker
                  timeZone={TIMEZONE}
                  name='end_date'
                  placeholder=''
                  disablePastDate
                  dateFormat={DISPLAY_FORMAT}
                  fullWidth
                />
              </StyledStackDatePicker>
              <Field.TimeSelect
                name='end_hour'
                label=''
                placeholder=''
                timeGap={15}
                inputFormat='hh:mm A'
                outputFormatLabel={TIME_HOUR_DISPLAY_FORMAT}
                fullWidth
                checkedValue
                resizePopover
              />
            </Box>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(51, 54, 61, 0.40)',
            }}
            variant='fs14'
          >
            Scheduled is based on Pacific Time.
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );
};

export default SchedulingSection;
